import { useCallback, useState } from "react";

const STORAGE_KEY = "signals-datagrid-visibility";

function readVisibilityMap(): Record<string, boolean> {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return {};

        const parsed = JSON.parse(stored);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }

        return parsed as Record<string, boolean>;
    } catch {
        return {};
    }
}

function readStoredVisibility(storageKey: string): boolean {
    return readVisibilityMap()[storageKey] === true;
}

function writeStoredVisibility(storageKey: string, isVisible: boolean) {
    try {
        const visibilityMap = readVisibilityMap();

        if (isVisible) {
            visibilityMap[storageKey] = true;
        } else {
            delete visibilityMap[storageKey];
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityMap));
    } catch {
        return;
    }
}

export function buildDataGridVisibilityKey(
    dashboardId: string | null,
    objectId: string,
): string {
    return `${dashboardId ?? "no-dashboard"}:${objectId}`;
}

export function useDataGridVisibility(storageKey: string) {
    const [visibilityState, setVisibilityState] = useState(() => ({
        storageKey,
        isVisible: readStoredVisibility(storageKey),
    }));

    if (visibilityState.storageKey !== storageKey) {
        setVisibilityState({
            storageKey,
            isVisible: readStoredVisibility(storageKey),
        });
    }

    const toggleVisibility = useCallback(() => {
        setVisibilityState((previous) => {
            const nextIsVisible = !previous.isVisible;
            writeStoredVisibility(storageKey, nextIsVisible);

            return { storageKey, isVisible: nextIsVisible };
        });
    }, [storageKey]);

    return {
        isDataGridVisible:
            visibilityState.storageKey === storageKey
                ? visibilityState.isVisible
                : readStoredVisibility(storageKey),
        toggleDataGridVisibility: toggleVisibility,
    };
}
